import { useEffect, useState } from "preact/hooks";
import type { ComponentChildren } from "preact";
import {
  archiveSources,
  clearAllFilters,
  clearCityFilter,
  clearComposerFilter,
  clearOrgFilter,
  clearPieceFilter,
  cities,
  composers,
  disabledSources,
  enableAllSources,
  exploreAlive,
  exploreEra,
  explorePremiere,
  exploreScope,
  exploreWindow,
  facetsLoaded,
  historicalCities,
  listDirection,
  listDirectionExplicit,
  orgs,
  radiusKm,
  resetMapToWorld,
  resetTimeWindow,
  selectedCityId,
  selectedComposerId,
  selectedCountry,
  selectedGenre,
  selectedOrgId,
  selectedPiece,
  selectedPieceKey,
  timeFrom,
  timeFromYear,
  timeTo,
  timeToYear,
  timeWindowFromURL,
  timeWindowTouched,
  viewMode,
  GENRE_OPTIONS,
  type ExploreScope,
} from "../state/store";
import { anniversaries, composerImpliedByPiece, eraLabel, exploreEras, exploreFocus, useFacet } from "../state/explore";
import { ensureFacets } from "../data/loader";
import { countryDisplayName } from "../utils/format";
import { FacetPanel } from "./explore/FacetPanel";
import { EntityHeader, FOCUS_LIMIT } from "./explore/EntityHeader";
import { ExploreSearch } from "./explore/ExploreSearch";
import { TimePanel } from "./explore/TimePanel";

const SCOPES: { value: ExploreScope; label: string; hint: string }[] = [
  { value: "upcoming", label: "Upcoming", hint: "from today" },
  { value: "past", label: "Past", hint: "past seasons + archives" },
  { value: "all", label: "All time", hint: "1600 → next season" },
];

interface Chip {
  key: string;
  label: string;
  /** Explore-only narrowings don't carry over to the map. */
  exploreOnly?: boolean;
  /** A breadcrumb chip: tapping it goes UP to that profile instead of removing a filter
   *  (the composer implied by a piece). */
  crumb?: boolean;
  onRemove: () => void;
}

/** Explore has two faces over the same slice Browse would list. With nothing selected it
 *  is a dashboard: the big dimensions as ranked panels, the power-user lenses folded away.
 *  With an entity selected (a piece, composer, organization, city or country — the most
 *  specific wins) it is that entity's PROFILE: a header of facts and headline numbers,
 *  then only the panels that say something about it. Tap any row to narrow; the sticky
 *  top holds the search box, the scope pills and the active chips; the sticky bottom is
 *  the one exit: "Show N concerts on the map". */
export function ExplorePage() {
  useEffect(() => {
    // Labels for every bucket resolve against composers.json / orgs.json.
    void ensureFacets().catch(() => {});
  }, []);

  // The Countries facet is the cheapest way to know the slice's size; the Places panel
  // and the profile header share the memoized request.
  const summary = useFacet("country");
  const eras = exploreEras.value;
  const scope = exploreScope.value;
  const w = exploreWindow.value;
  const focus = exploreFocus.value;
  const [moreOpen, setMoreOpen] = useState(false);

  const chips = buildChips();
  const exploreOnlyActive = chips.some((c) => c.exploreOnly);

  const thisYear = new Date().getFullYear();
  const annivYears = scope === "upcoming" && !focus ? [thisYear, thisYear + 1] : [];
  const annivs = facetsLoaded.value && annivYears.length ? anniversaries(annivYears) : [];

  function afterDrill() {
    if (window.scrollY > 160) window.scrollTo({ top: 0, behavior: "smooth" });
  }

  function showOnMap() {
    listDirection.value = scope === "past" ? "backward" : "forward";
    listDirectionExplicit.value = true;
    resetMapToWorld();
    viewMode.value = "browse";
    requestAnimationFrame(() => {
      document.querySelector(".map-pane")?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  }

  const live = eras.includes("live");
  const archive = eras.includes("archive");
  const kind = focus?.kind ?? null;

  // ── Which panels this face shows ──
  // Dimensions already fixed by a filter never get a panel (a country under a country
  // filter is one bar). Under a focus, the rule is "what does this entity have":
  //   piece      → who performs it, when, where
  //   composer   → their own works, who performs them, when, where
  //   org        → what it plays (composers, pieces), when, genre, cities if it tours
  //   city       → what is played there, by whom, when, genre
  //   country    → its cities, then as a city
  const showComposers = selectedComposerId.value === null && kind !== "piece";
  const showPieces = selectedPieceKey.value === null;
  const showOrgs = live && selectedOrgId.value === null;
  const showCountries = !selectedCountry.value && kind !== "org" && kind !== "city";
  const showCities = selectedCityId.value === null;
  const showGenre = live && !selectedGenre.value && (kind === null || kind === "org" || kind === "city" || kind === "country");
  const showMore = kind !== "piece" && kind !== "composer";
  const bigLimit = focus ? FOCUS_LIMIT : 50;

  // Under a focus a one-row panel is a fact the header already states ("1 country"), not a chart.
  const single = focus !== null;
  const composersPanel = showComposers && (
    <FacetPanel
      key="composers"
      dim="composer"
      lens
      limit={bigLimit}
      hideIfSingle={single}
      hint={kind === "org" ? "Whose music it performs" : kind === "city" || kind === "country" ? "Whose music is performed here" : undefined}
      onDrill={afterDrill}
    />
  );
  const piecesPanel = showPieces && (
    <FacetPanel
      key="pieces"
      dim="piece"
      lens={selectedComposerId.value === null}
      own={selectedComposerId.value !== null ? "only" : null}
      title={kind === "composer" ? "Most performed works" : undefined}
      hint={kind === "composer" ? "Ranked by number of concerts" : undefined}
      onDrill={afterDrill}
    />
  );
  const orgsPanel = showOrgs && (
    <FacetPanel
      key="orgs"
      dim="org"
      lens
      limit={bigLimit}
      hideIfSingle={single}
      title={kind === "piece" || kind === "composer" ? "Performed by" : undefined}
      onDrill={afterDrill}
    />
  );
  const timePanel = <TimePanel key="time" onDrill={afterDrill} />;
  const countriesPanel = showCountries && <FacetPanel key="countries" dim="country" lens hideIfSingle={single} onDrill={afterDrill} />;
  const citiesPanel = showCities && (
    <FacetPanel
      key="cities"
      dim="city"
      lens
      limit={bigLimit}
      hideIfSingle={single}
      title={selectedCountry.value ? `Cities in ${countryDisplayName(selectedCountry.value)}` : undefined}
      hint={kind === "org" ? "Where it performs" : undefined}
      onDrill={afterDrill}
    />
  );
  const genrePanel = showGenre && <FacetPanel key="genre" dim="genre" onDrill={afterDrill} />;

  let panels: ComponentChildren[];
  switch (kind) {
    case "piece":
      panels = [orgsPanel, countriesPanel, citiesPanel, timePanel];
      break;
    case "composer":
      panels = [piecesPanel, orgsPanel, countriesPanel, citiesPanel, timePanel];
      break;
    case "org":
      panels = [composersPanel, piecesPanel, genrePanel, citiesPanel, timePanel];
      break;
    case "city":
      panels = [composersPanel, piecesPanel, orgsPanel, genrePanel, timePanel];
      break;
    case "country":
      panels = [citiesPanel, composersPanel, piecesPanel, orgsPanel, genrePanel, timePanel];
      break;
    default:
      panels = [composersPanel, piecesPanel, orgsPanel, timePanel, countriesPanel, citiesPanel, genrePanel];
      // (When spans the full row from 720px — see .xp-panel-time — so the landing reads as
      // "who and what", then "when", then "where".)
  }

  type MoreDef = { key: string; label: string; node: ComponentChildren };
  const moreDefs: MoreDef[] = showMore
    ? ([
        exploreEra.value === null && {
          key: "era",
          label: "composer era",
          // A chronological ribbon — always whole, or the collapsed head would be the
          // medieval tail rather than the canon.
          node: (
            <FacetPanel
              key="era"
              dim="era"
              rows={30}
              hint={exploreEra.value !== null ? undefined : "Concerts by the birth half-century of the composers on the programme"}
              onDrill={afterDrill}
            />
          ),
        },
        !exploreAlive.value && {
          key: "alive",
          label: "living composers",
          node: <FacetPanel key="alive" dim="alive" hint="Concerts with a composer alive when they took place" onDrill={afterDrill} />,
        },
        live && kind !== "org" && { key: "org_type", label: "organization type", node: <FacetPanel key="org_type" dim="org_type" onDrill={afterDrill} /> },
        archive && { key: "source", label: "archive sources", node: <FacetPanel key="source" dim="source" onDrill={afterDrill} /> },
        archive && !explorePremiere.value && { key: "premiere", label: "premieres", node: <FacetPanel key="premiere" dim="premiere" onDrill={afterDrill} /> },
      ] as (MoreDef | false)[]).filter((d): d is MoreDef => Boolean(d))
    : [];
  const morePanels = moreDefs.map((d) => d.node);
  const moreHint = moreDefs.map((d) => d.label).join(", ");

  return (
    <div class={`xp${focus ? " has-focus" : ""}`}>
      <div class="xp-top">
        <ExploreSearch onPick={afterDrill} />
        <div class="xp-scope" role="group" aria-label="Time scope">
          {SCOPES.map((s) => (
            <button
              key={s.value}
              type="button"
              class={`xp-scope-btn${scope === s.value ? " active" : ""}`}
              aria-pressed={scope === s.value}
              title={s.hint}
              onClick={() => {
                exploreScope.value = s.value;
              }}
            >
              {s.label}
            </button>
          ))}
        </div>
        {chips.length > 0 && (
          <div class="xp-chips" aria-label="Active filters">
            {chips.map((c) => (
              <button
                key={c.key}
                type="button"
                class={`xp-chip${c.exploreOnly ? " is-explore-only" : ""}${c.crumb ? " is-crumb" : ""}`}
                onClick={c.onRemove}
                title={c.crumb ? `Back to ${c.label}` : `Remove: ${c.label}`}
              >
                <span class="xp-chip-label">{c.label}</span>
                <span class="xp-chip-x" aria-hidden="true">{c.crumb ? "›" : "×"}</span>
              </button>
            ))}
            {chips.length > 1 && (
              <button type="button" class="xp-chip xp-chip-clear" onClick={clearAllFilters}>
                Clear all
              </button>
            )}
          </div>
        )}
      </div>

      {focus && <EntityHeader key={`${focus.kind}:${focus.key}`} focus={focus} onDrill={afterDrill} />}

      {/* Dimmed while the slice is being recounted — a stale total beside a fresh chip would
          read as the answer. Under a focus the header carries the count; this line keeps
          only the window. */}
      <p class={`xp-summary${summary.loading ? " is-loading" : ""}`} aria-live="polite">
        {focus ? (
          // "All time · all time" would be silly — the scope word only where it adds something.
          capitalize(scope === "all" ? describeWindow(scope, w.from, w.to, eras) : `${SCOPES.find((s) => s.value === scope)?.label ?? ""} · ${describeWindow(scope, w.from, w.to, eras)}`)
        ) : summary.loading && summary.matched === 0 ? (
          "Counting…"
        ) : (
          <>
            <strong>{summary.matched.toLocaleString()}</strong> concert{summary.matched === 1 ? "" : "s"}
            {scope !== "upcoming" && live && summary.matchedUp > 0 && (
              <> · {summary.matchedUp.toLocaleString()} upcoming</>
            )}
            {" · "}
            {describeWindow(scope, w.from, w.to, eras)}
            <span class="xp-summary-hint"> · Tap a composer, piece, organization or place to open its profile</span>
          </>
        )}
      </p>

      <div class="xp-grid">
        {panels}

        {annivs.length > 0 && (
          <section class="xp-panel">
            <header class="xp-panel-head">
              <div class="xp-panel-titles">
                <h3 class="xp-panel-title">Anniversaries</h3>
                <p class="xp-panel-hint">Round birth and death anniversaries in {annivYears.join(" and ")}</p>
              </div>
            </header>
            <ol class="xp-rows">
              {annivs.map((a) => (
                <li key={`${a.composerId}-${a.kind}-${a.year}`}>
                  <button
                    type="button"
                    class="xp-row xp-row-stack"
                    onClick={() => {
                      selectedComposerId.value = a.composerId;
                      afterDrill();
                    }}
                    title={`Narrow to ${a.name}`}
                  >
                    <span class="xp-row-main">
                      <span class="xp-row-name">
                        {a.name}
                        <span class="xp-row-detail">
                          {a.year}: {a.years} years since {a.kind === "birth" ? "birth" : "death"} in {a.since}
                        </span>
                      </span>
                    </span>
                    <span class="xp-row-num">
                      <span class="xp-row-n">{a.count.toLocaleString()}</span>
                      <span class="xp-row-sub">concerts, all time</span>
                    </span>
                  </button>
                </li>
              ))}
            </ol>
          </section>
        )}
      </div>

      {morePanels.length > 0 && (
        <div class="xp-more">
          {/* A lens in use (era / living / premiere chip) keeps its panels open: the
              reader must be able to see and change what they narrowed by. */}
          {exploreOnlyActive ? (
            <h3 class="xp-more-title">More lenses</h3>
          ) : (
            <button type="button" class="xp-more-btn" aria-expanded={moreOpen} onClick={() => setMoreOpen((v) => !v)}>
              {moreOpen ? "Fewer lenses" : "More lenses"}
              <span class="xp-more-hint"> · {moreHint}</span>
            </button>
          )}
          {(moreOpen || exploreOnlyActive) && <div class="xp-grid">{morePanels}</div>}
        </div>
      )}

      <div class="xp-cta">
        <button type="button" class={`xp-cta-btn${summary.loading ? " is-loading" : ""}`} onClick={showOnMap}>
          Show {summary.matched > 0 ? summary.matched.toLocaleString() : ""} concert{summary.matched === 1 ? "" : "s"} on the map →
        </button>
        {exploreOnlyActive && (
          <span class="xp-cta-note">Era, living-composer and premiere filters apply only here.</span>
        )}
      </div>
    </div>
  );
}

const MONTHS_LONG = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

/** "Nov 2026" for a one-month window, "2026" for a whole year, else "2026–2027". */
function windowChipLabel(from: string, to: string, fromYear: number, toYear: number): string {
  if (from && to && from.slice(0, 7) === to.slice(0, 7)) {
    return `${MONTHS_LONG[Number(from.slice(5, 7)) - 1]?.slice(0, 3) ?? ""} ${from.slice(0, 4)}`;
  }
  return fromYear === toYear ? String(fromYear) : `${fromYear}–${toYear}`;
}

function capitalize(t: string): string {
  return t.charAt(0).toUpperCase() + t.slice(1);
}

/** "Nov 30, 2026" from an ISO date. */
function niceDate(iso: string): string {
  const y = iso.slice(0, 4);
  const m = Number(iso.slice(5, 7));
  const d = Number(iso.slice(8, 10));
  if (!m || !d) return iso.slice(0, 10);
  return `${MONTHS_LONG[m - 1]?.slice(0, 3) ?? ""} ${d}, ${y}`;
}

function describeWindow(scope: ExploreScope, from: string | null, to: string | null, eras: string[]): string {
  const parts: string[] = [];
  if (scope === "upcoming") parts.push(to ? `from today to ${niceDate(to)}` : "from today");
  else if (scope === "past") parts.push(from ? `${niceDate(from)} to yesterday` : "everything before today");
  else parts.push(from || to ? `${from ? niceDate(from) : "1600"} to ${to ? niceDate(to) : "next season"}` : "all time");
  if (eras.length === 2) parts.push("live listings + archives");
  else if (eras[0] === "archive") parts.push("archives only");
  else if (eras.length === 0) parts.push("no sources enabled");
  return parts.join(" · ");
}

function buildChips(): Chip[] {
  const chips: Chip[] = [];
  const cid = selectedComposerId.value;
  if (cid !== null) {
    const label = composers.value.find((c) => c.id === cid)?.name ?? "Composer";
    if (composerImpliedByPiece.value) {
      // Under a piece the composer is context, not a removable narrowing: a crumb that
      // steps back up to the composer's profile.
      chips.push({ key: "composer", label, crumb: true, onRemove: clearPieceFilter });
    } else {
      chips.push({ key: "composer", label, onRemove: clearComposerFilter });
    }
  }
  if (selectedPieceKey.value) {
    chips.push({ key: "piece", label: selectedPiece.value?.label ?? "Piece", onRemove: clearPieceFilter });
  }
  const oid = selectedOrgId.value;
  if (oid !== null) {
    chips.push({ key: "org", label: orgs.value.find((o) => o.id === oid)?.name ?? "Organization", onRemove: clearOrgFilter });
  }
  const cityId = selectedCityId.value;
  if (cityId !== null) {
    const city = cities.value.find((c) => c.id === cityId) ?? historicalCities.value.find((c) => c.id === cityId);
    const km = radiusKm.value;
    chips.push({ key: "city", label: `${city?.name ?? "City"}${km ? ` +${km} km` : ""}`, onRemove: clearCityFilter });
  }
  if (selectedCountry.value) {
    chips.push({ key: "country", label: countryDisplayName(selectedCountry.value), onRemove: () => { selectedCountry.value = ""; } });
  }
  if (selectedGenre.value) {
    chips.push({ key: "genre", label: GENRE_OPTIONS.find((g) => g.value === selectedGenre.value)?.label ?? selectedGenre.value, onRemove: () => { selectedGenre.value = ""; } });
  }
  if (timeWindowTouched.value || timeWindowFromURL.value) {
    chips.push({ key: "window", label: windowChipLabel(timeFrom.value, timeTo.value, timeFromYear.value, timeToYear.value), onRemove: resetTimeWindow });
  }
  if (disabledSources.value.size > 0) {
    const total = archiveSources.value.length + 1;
    const hidden = disabledSources.value.size;
    chips.push({ key: "sources", label: `${Math.max(0, total - hidden)} of ${total} sources`, onRemove: enableAllSources });
  }
  if (exploreEra.value !== null) {
    // The filter is per CONCERT (any composer on the programme qualifies), so the chip says so.
    chips.push({ key: "era", label: `With a composer ${eraLabel(String(exploreEra.value)).toLowerCase()}`, exploreOnly: true, onRemove: () => { exploreEra.value = null; } });
  }
  if (exploreAlive.value) {
    chips.push({ key: "alive", label: "With a living composer", exploreOnly: true, onRemove: () => { exploreAlive.value = false; } });
  }
  if (explorePremiere.value) {
    chips.push({ key: "premiere", label: "Premieres", exploreOnly: true, onRemove: () => { explorePremiere.value = false; } });
  }
  return chips;
}
